import { Mail, MapPin, Phone, ArrowUpRight, Clock3, ShieldCheck, UserCheck } from "lucide-react";
import { Reveal, Chapter } from "./Reveal";

const AGENTCIS_FORM_URL = "https://dpa.agentcisapp.com/online-form/directive-pathway-enquiry";

const EXPECTATIONS = [
  { icon: Clock3, text: "Takes about 2 minutes to complete" },
  { icon: ShieldCheck, text: "Goes straight into our client system — nothing gets missed" },
  { icon: UserCheck, text: "An advisor personally reviews every enquiry" },
];

export default function EnquiryForm() {
  return (
    <section id="contact" className="relative overflow-hidden bg-gradient-to-b from-white via-sky-50 to-sky-100 py-24 sm:py-32">
      <div className="pointer-events-none absolute -left-24 top-24 h-[360px] w-[360px] rounded-full bg-sky-200/50 blur-3xl" />
      <div className="relative mx-auto grid max-w-7xl items-center gap-14 px-5 sm:px-8 lg:grid-cols-2">
        <div>
          <Reveal>
            <Chapter number="05" label="For Consultation" />
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-5 font-display text-3xl font-extrabold tracking-[-0.03em] text-slate-900 sm:text-4xl lg:text-5xl">
              Get in Touch — <span className="text-sky-600">We're Here to Help</span>
            </h2>
          </Reveal>
          <Reveal delay={0.18}>
            <p className="mt-6 max-w-md text-base leading-relaxed text-slate-600 sm:text-lg">
              Tell us a little about your goals and one of our advisors will reach
              out with honest, personalised guidance — completely free.
            </p>
          </Reveal>
          <Reveal delay={0.26}>
            <div className="mt-8 space-y-4">
              <a
                data-testid="contact-email-link"
                href="mailto:admin@directivepathway.com.au"
                className="flex items-center gap-3 text-sm font-medium text-slate-700 transition-colors hover:text-sky-700"
              >
                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-white text-sky-600 ring-1 ring-sky-200">
                  <Mail className="h-4 w-4" />
                </span>
                admin@directivepathway.com.au
              </a>
              <div className="flex items-center gap-3 text-sm font-medium text-slate-700">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white text-sky-600 ring-1 ring-sky-200">
                  <Phone className="h-4 w-4" />
                </span>
                <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
                  {[
                    { num: "0449 132 525", tel: "0449132525", testid: "contact-phone-link" },
                    { num: "0402 207 660", tel: "0402207660", testid: "contact-phone-link-2" },
                    { num: "0403 811 726", tel: "0403811726", testid: "contact-phone-link-3" },
                  ].map((p, i) => (
                    <span key={p.tel} className="flex items-center gap-2">
                      {i > 0 && <span className="text-sky-300">·</span>}
                      <a data-testid={p.testid} href={`tel:${p.tel}`} className="transition-colors hover:text-sky-700">
                        {p.num}
                      </a>
                    </span>
                  ))}
                </span>
              </div>
              <div className="flex items-center gap-3 text-sm font-medium text-slate-700">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white text-sky-600 ring-1 ring-sky-200">
                  <MapPin className="h-4 w-4" />
                </span>
                Level 6/607 Bourke Street, Melbourne VIC 3000
              </div>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.15}>
          <div
            data-testid="enquiry-form-container"
            className="rounded-3xl border border-sky-100 bg-white/90 p-8 text-center shadow-[0_30px_80px_rgba(2,132,199,0.15)] backdrop-blur sm:p-12"
          >
            <img
              src="/logo-symbol.png"
              alt="Directive Pathway logo"
              className="mx-auto h-16 w-16 object-contain"
            />
            <h3 className="mt-6 font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
              Start Your Enquiry
            </h3>
            <p className="mx-auto mt-3 max-w-sm text-sm leading-relaxed text-slate-600 sm:text-base">
              Complete our quick enquiry form and your details go straight to our
              advising team — we'll get back to you with honest, tailored guidance.
            </p>
            <a
              data-testid="agentcis-form-link"
              href={AGENTCIS_FORM_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="group mt-8 inline-flex items-center gap-2.5 rounded-full bg-sky-600 px-8 py-4 text-sm font-semibold text-white shadow-[0_12px_36px_rgba(2,132,199,0.4)] transition-all duration-300 hover:-translate-y-1 hover:bg-sky-500 hover:shadow-[0_18px_44px_rgba(2,132,199,0.5)]"
            >
              Open Enquiry Form
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
            <div className="mt-9 space-y-3 border-t border-sky-100 pt-7 text-left">
              {EXPECTATIONS.map((e) => (
                <p key={e.text} className="flex items-center gap-3 text-sm text-slate-600">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-sky-50 text-sky-600 ring-1 ring-sky-100">
                    <e.icon className="h-4 w-4" />
                  </span>
                  {e.text}
                </p>
              ))}
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
